import { Directive, OnDestroy } from '@angular/core';
import { Observable, Subject } from 'rxjs';

import { TableConstructorData, TableConstructorDataObject } from 'src/app/interfaces/tables.interfaces';
import {
  PendingUserInvolvement,
  PendingUserInvolvementInCommunity,
  PendingUserInvolvementInProject,
} from 'src/app/swagger-api';
import { UserTableCreators } from 'src/app/views/table-users-list/adapters/table-creator.enum';
import { TableCreatorFactory } from 'src/app/views/table-users-list/adapters/table-creator.method';

@Directive()
export abstract class SocialInitiativePostsShared implements OnDestroy {
  protected userRequests: PendingUserInvolvement[] = [];
  protected userPersonalInvites: PendingUserInvolvementInCommunity[] = [];
  protected userProjectInvites: PendingUserInvolvementInProject[] = [];
  protected invites: PendingUserInvolvement[] = [];

  private tableDataChanges$: Subject<TableConstructorData> = new Subject();

  public get tableDataChanged(): Observable<TableConstructorData> {
    return this.tableDataChanges$.asObservable();
  }

  public ngOnDestroy(): void {
    this.tableDataChanges$.complete();
  }

  /**
   * Handles either rejection or confirmation for incoming community request from other
   * user asking to join current user's community
   *
   * @param userId - user ID an invitation has been processed with
   * @param entityId community Id an invitation has been processed with
   */
  protected updateUserRequests(userId: string, entityId: string): void {
    this.reduceUserRequests(userId);
    this.notifyTableDataChanges(UserTableCreators.ENTITY_USERS_REQUESTS, {
      usersRequests: this.userRequests,
      entityId,
      allowManaging: true,
    });
  }

  /**
   * Handles either rejection or confirmation for incoming request from other user
   * inviting logged in user to join their community
   *
   * @param actorId - user ID an invitation has been processed with
   * @param entityId - community Id an invitation has been processed with
   */
  protected updateUserPersonalInvites(actorId: string, entityId: string): void {
    this.reduceUserPersonalInviteRequests(entityId);
    this.notifyTableDataChanges(UserTableCreators.PERSONAL_USER_INVITES_FOR_COMMUNITY, {
      personalEntities: this.userPersonalInvites,
      actorId,
    });
  }

  /**
   * Handles either rejection or confirmation for incoming request from other user
   * inviting logged in user to join the project
   *
   * @param actorId - user ID an invitation has been processed with
   * @param entityId - project Id an invitation has been processed with
   */
  protected updateUserProjectInvites(actorId: string, entityId: string): void {
    this.reduceUserProjectInviteRequests(entityId);
    this.notifyTableDataChanges(UserTableCreators.PERSONAL_USER_INVITES_FOR_PROJECT, {
      personalEntities: this.userProjectInvites,
      actorId,
    });
  }

  /**
   * Notifies tableDataChanges$ the change has happened
   *
   * @param type {UserTableCreators} data
   * @param data {TableConstructorDataObject}
   */
  protected notifyTableDataChanges(type: UserTableCreators, data: TableConstructorDataObject): void {
    this.tableDataChanges$.next(TableCreatorFactory.createTableConstructorData(type, data));
  }

  /**
   * Updates the list with community request from other user removing the given user
   *
   * @param userId - user ID to be deleted
   */
  private reduceUserRequests(userId: string): void {
    this.userRequests = this.userRequests.filter((userRequest: PendingUserInvolvement) => {
      return userRequest.user.user_id !== userId;
    });
  }

  /**
   * Updates the list with invitation community request from other user removing the given user
   *
   * @param entityId - entity ID to be deleted
   */
  private reduceUserPersonalInviteRequests(entityId: string): void {
    this.userPersonalInvites = this.userPersonalInvites.filter(
      (userRequestFromCommunity: PendingUserInvolvementInCommunity) => {
        return userRequestFromCommunity.community.entity_id !== entityId;
      },
    );
  }

  /**
   * Updates the list with invitation project request from other user removing the given project
   *
   * @param entityId - project ID to be deleted
   */

  private reduceUserProjectInviteRequests(entityId: string): void {
    this.userProjectInvites = this.userProjectInvites.filter(
      (userRequestFromProject: PendingUserInvolvementInProject) => {
        return userRequestFromProject.project.entity_id !== entityId;
      },
    );
  }
}
